import React, {useEffect, useState} from "react";
import "./testSidebar.css";
import {Menu, MenuItem, Sidebar, SubMenu, useProSidebar} from "react-pro-sidebar";
import {Link, useLocation} from "react-router-dom";
import {
    AutoGraphOutlined,
    BackupOutlined,
    BusinessOutlined,
    ChatBubbleOutlineOutlined,
    DashboardOutlined,
    ElderlyOutlined,
    GroupsOutlined,
    LogoutOutlined,
    MonetizationOnOutlined,
    MoneyOffCsredOutlined,
    PaymentsOutlined,
    PeopleOutlined,
    SettingsOutlined,
} from "@mui/icons-material";
import MenuOutlinedIcon from "@mui/icons-material/MenuOutlined";
import {useMediaQuery, Badge, Avatar, Typography, Divider, Tooltip, Box, IconButton} from "@mui/material";
import CryptoJS from "crypto-js";
import logo from "../../assets/react-logo-no-background.svg";
import {KEY} from "../../dummyData";

// Define color constants for consistency
const COLORS = {
    primary: "#2C5283",
    secondary: "#E2684F",
    tertiary: "#4CAF50",
    background: "#FFFFFF",
    backgroundAlt: "#F3F7FA",
    hover: "#EDF2F7",
    active: "#E1EDF7",
    text: "#2C3E50",
    textLight: "#6B7C93",
    border: "#E2E8F0",
    divider: "#EDF2F7",
};

export default function TestSidebar() {
    const isMobile = useMediaQuery("(max-width:800px)");
    const {collapseSidebar, collapsed} = useProSidebar();
    const location = useLocation();

    const decryptLocalStorage = () => {
        try {
            const encryptedData = localStorage.getItem("myMap");
            if (!encryptedData) return "";
            const decryptedData = CryptoJS.AES.decrypt(encryptedData, KEY).toString(CryptoJS.enc.Utf8);
            return JSON.parse(decryptedData);
        } catch {
            return null;
        }
    };

    const [originalText, setOriginalText] = useState(decryptLocalStorage());
    const [logoVisible, setLogoVisible] = useState(true);
    const [expandedSubmenus, setExpandedSubmenus] = useState({
        tenants: false,
        money: false,
    });

    // Get user info from originalText
    const userName = originalText && originalText[1] ? originalText[1][1] : "User";
    const userRole = originalText && originalText[2] ? originalText[2][1] : "Property Manager";

    // Check if path is active (for styling)
    const isActive = (path) => {
        if (typeof path === 'string') {
            return location.pathname === path;
        } else if (Array.isArray(path)) {
            return path.some(p => location.pathname.includes(p));
        }
        return false;
    };

    // Handle submenu expansion
    const handleSubmenuExpand = (submenu) => {
        setExpandedSubmenus({
            ...expandedSubmenus,
            [submenu]: !expandedSubmenus[submenu],
        });
    };

    useEffect(() => {
        // Initialize expanded submenus based on current path
        setExpandedSubmenus({
            tenants: location.pathname.includes('tenant'),
            money: location.pathname.includes('transactions') || location.pathname.includes('expenses'),
        });

        if (isMobile) {
            collapseSidebar();
            setLogoVisible(false);
        }
    }, [isMobile, collapseSidebar, location.pathname]);

    const logout = () => {
        localStorage.clear();
        window.location.href = "/";
    };

    const menuItemStyles = {
        root: {
            fontSize: "14px",
            fontWeight: 500,
        },
        button: ({level, active}) => ({
            color: active ? COLORS.primary : COLORS.text,
            backgroundColor: active ? COLORS.active : "transparent",
            borderLeft: active ? `3px solid ${COLORS.primary}` : "3px solid transparent",
            borderRadius: "0",
            fontWeight: active ? 600 : 500,
            padding: level === 0 ? "10px 16px" : "8px 16px 8px 32px",
            transition: "all 0.2s ease-in-out",
            "&:hover": {
                backgroundColor: COLORS.hover,
                borderLeft: active
                    ? `3px solid ${COLORS.primary}`
                    : `3px solid ${COLORS.secondary}`,
            },
        }),
        icon: {
            color: COLORS.primary,
            fontSize: "1.2rem",
        },
        SubMenuExpandIcon: {
            color: COLORS.text,
        },
        subMenuContent: ({level}) => ({
            backgroundColor: COLORS.backgroundAlt,
        }),
        label: ({open, active}) => ({
            fontWeight: active ? 600 : 500,
        }),
    };

    // Custom Sidebar Header component
    const SidebarHeader = () => (
        <Box
            sx={{
                padding: collapsed ? "16px 8px" : "16px",
                display: "flex",
                flexDirection: collapsed ? "column" : "row",
                alignItems: "center",
                justifyContent: collapsed ? "center" : "space-between",
                borderBottom: `1px solid ${COLORS.divider}`,
                marginBottom: "8px",
            }}
        >
           {/* {(!collapsed || isMobile) ? (
                <IconButton
                    sx={{color: COLORS.primary}}
                    onClick={() => {
                        if (!isMobile) setLogoVisible(!logoVisible);
                        collapseSidebar();
                    }}
                >
                    {collapsed ? <ChevronRight/> : <ChevronLeft/>}
                </IconButton>
            ) : null}*/}

            {!collapsed && logoVisible && (
                <img
                    src={logo}
                    alt="Company Logo"
                    style={{
                        height: "80px",
                        width: "auto",
                    }}
                />
            )}

            {collapsed && (
                <IconButton
                    sx={{
                        color: COLORS.primary,
                        mt: 1
                    }}
                    onClick={() => {
                        if (!isMobile) setLogoVisible(!logoVisible);
                        collapseSidebar();
                    }}
                >
                    <MenuOutlinedIcon/>
                </IconButton>
            )}
        </Box>
    );

    // Custom User Profile Component
    const UserProfile = () => (
        <Box
            sx={{
                padding: collapsed ? "8px" : "16px",
                display: "flex",
                flexDirection: collapsed ? "column" : "row",
                alignItems: "center",
                gap: collapsed ? 1 : 2,
                borderBottom: `1px solid ${COLORS.divider}`,
                marginBottom: "16px",
            }}
        >
            <Avatar
                sx={{
                    bgcolor: COLORS.primary,
                    width: collapsed ? 36 : 40,
                    height: collapsed ? 36 : 40,
                }}
            >
                {userName.charAt(0)}
            </Avatar>

            {!collapsed && (
                <Box sx={{flex: 1, overflow: "hidden"}}>
                    <Typography
                        variant="subtitle2"
                        sx={{
                            fontWeight: 600,
                            color: COLORS.text,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                        }}
                    >
                        {userName}
                    </Typography>
                    <Typography
                        variant="caption"
                        sx={{
                            color: COLORS.textLight,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                        }}
                    >
                        {userRole}
                    </Typography>
                </Box>
            )}

            {collapsed && (
                <Tooltip title={`${userName} - ${userRole}`} placement="right">
                    <Box></Box>
                </Tooltip>
            )}
        </Box>
    );

    const renderMenuItems = () => (
        <>
            <MenuItem
                active={isActive("/home")}
                component={<Link to="/home"/>}
                icon={<DashboardOutlined/>}
            >
                Dashboard
            </MenuItem>

            <MenuItem
                active={isActive("/analytics")}
                component={<Link to="/analytics"/>}
                icon={<AutoGraphOutlined/>}
            >
                Analytics
            </MenuItem>

            <SubMenu
                label="Tenants"
                icon={<GroupsOutlined/>}
                onOpenChange={() => handleSubmenuExpand('tenants')}
                open={expandedSubmenus.tenants}
                active={isActive(["tenant", "tenantList", "vacatingTenantList", "oldTenantList"])}
            >
                <MenuItem
                    active={isActive("/tenantList")}
                    component={<Link to="/tenantList"/>}
                    icon={<PeopleOutlined/>}
                >
                    All Tenants
                </MenuItem>
                <MenuItem
                    active={isActive("/vacatingTenantList")}
                    component={<Link to="/vacatingTenantList"/>}
                    icon={<PeopleOutlined/>}
                >
                    Vacating Tenants
                </MenuItem>
               {/* <MenuItem
                    active={isActive("/oldTenantList")}
                    component={<Link to="/oldTenantList"/>}
                    icon={<ElderlyOutlined/>}
                >
                    Old Tenants
                </MenuItem>*/}
            </SubMenu>

            <MenuItem
                active={isActive("/propertyList")}
                component={<Link to="/propertyList"/>}
                icon={<BusinessOutlined/>}
            >
                Properties
            </MenuItem>

            <SubMenu
                label="Finance"
                icon={<MonetizationOnOutlined/>}
                onOpenChange={() => handleSubmenuExpand('money')}
                open={expandedSubmenus.money}
                active={isActive(["transactions", "expenses"])}
            >
                <MenuItem
                    active={isActive("/transactions")}
                    component={<Link to="/transactions"/>}
                    icon={<PaymentsOutlined/>}
                >
                    Transactions
                </MenuItem>
                <MenuItem
                    active={isActive("/expenses")}
                    component={<Link to="/expenses"/>}
                    icon={<MoneyOffCsredOutlined/>}
                >
                    Expenses
                </MenuItem>
            </SubMenu>

            <MenuItem
                active={isActive("/tenantIssues")}
                component={<Link to="/tenantIssues"/>}
                icon={<BusinessOutlined/>}
            >
                Tenant Issues
            </MenuItem>

           {/* <MenuItem
                active={isActive("/messages")}
                component={<Link to="/messages"/>}
                icon={<ChatBubbleOutlineOutlined/>}
            >
                Messages
            </MenuItem>*/}

            <MenuItem
                active={isActive("/backup")}
                component={<Link to="/backup"/>}
                icon={<BackupOutlined/>}
            >
                Backup
            </MenuItem>

            <Divider
                sx={{
                    my: 2,
                    mx: collapsed ? 1 : 2,
                    borderColor: COLORS.divider
                }}
            />
{/*
            <MenuItem
                active={isActive("/settings")}
                component={<Link to="/settings"/>}
                icon={<SettingsOutlined/>}
            >
                Settings
            </MenuItem>*/}

            <MenuItem
                onClick={logout}
                icon={<LogoutOutlined style={{color: COLORS.secondary}}/>}
            >
                Logout
            </MenuItem>
        </>
    );

    return (
        <div
            style={{
                zIndex: 1000,
                display: ["/", "/signup"].includes(location.pathname) ? "none" : "",
                height: "100%",
                position: "fixed", // Fixed position so it doesn't scroll with page content
                left: 0,
                top: 0,
                bottom: 0,
            }}
        >
            <Sidebar
                style={{
                    height: "100%", // Full height
                    position: "relative", // Changed from sticky to relative
                    border: "none",
                    boxShadow: "rgba(0, 0, 0, 0.1) 0px 1px 3px 0px, rgba(0, 0, 0, 0.06) 0px 1px 2px 0px",
                    backgroundColor: COLORS.background,
                    display: "flex",
                    flexDirection: "column",
                }}
                // width="250px"
                // collapsedWidth="70px"
                defaultCollapsed={isMobile}
            >
                <SidebarHeader/>
                <UserProfile/>

                {/* Scrollable menu container */}
                <Box
                    sx={{
                        flex: 1,
                        overflowY: "auto", // Only the menu content scrolls
                        overflowX: "hidden",
                        "&::-webkit-scrollbar": {
                            width: "6px",
                        },
                        "&::-webkit-scrollbar-track": {
                            background: COLORS.backgroundAlt,
                        },
                        "&::-webkit-scrollbar-thumb": {
                            background: COLORS.border,
                            borderRadius: "6px",
                        },
                        "&::-webkit-scrollbar-thumb:hover": {
                            background: COLORS.textLight,
                        },
                    }}
                >
                    <Menu menuItemStyles={menuItemStyles}>
                        {renderMenuItems()}
                    </Menu>
                </Box>
            </Sidebar>
        </div>
    );
}